import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styles from './Sidebar.module.css';
import { selectUserRole, selectSession, selectLogo } from '../../store/selectors/authSelectors';
import { User } from '../../types';

// SVG Icons
const DashboardIcon: React.FC<{ isActive: boolean }> = ({ isActive }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.sidebarIcon}
  >
    <path
      d="M3 13H11V3H3V13ZM3 21H11V15H3V21ZM13 21H21V11H13V21ZM13 3V9H21V3H13Z"
      fill={isActive ? 'white' : '#005A9C'}
    />
  </svg>
);

const AdminIcon: React.FC<{ isActive: boolean }> = ({ isActive }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.sidebarIcon}
  >
    <path
      d="M12 15.5C10.07 15.5 8.5 13.93 8.5 12C8.5 10.07 10.07 8.5 12 8.5C13.93 8.5 15.5 10.07 15.5 12C15.5 13.93 13.93 15.5 12 15.5ZM12 10C10.62 10 9.5 11.12 9.5 12.5C9.5 13.88 10.62 15 12 15C13.38 15 14.5 13.88 14.5 12.5C14.5 11.12 13.38 10 12 10Z"
      fill={isActive ? 'white' : '#005A9C'}
    />
    <path
      d="M19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.35 19.43 11.03L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.65 15.5 5.32 14.87 5.07L14.49 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.51 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11.03C4.53 11.35 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.95C7.96 18.35 8.5 18.68 9.13 18.93L9.51 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.49 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.95L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97ZM12 15.5C10.07 15.5 8.5 13.93 8.5 12C8.5 10.07 10.07 8.5 12 8.5C13.93 8.5 15.5 10.07 15.5 12C15.5 13.93 13.93 15.5 12 15.5Z"
      fill={isActive ? 'white' : '#005A9C'}
    />
  </svg>
);

const HistoryIcon: React.FC<{ isActive: boolean }> = ({ isActive }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.sidebarIcon}
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z"
      fill={isActive ? 'white' : '#005A9C'}
    />
  </svg>
);

const BillingIcon: React.FC<{ isActive: boolean }> = ({ isActive }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.sidebarIcon}
  >
    <path
      d="M20 4H4C2.89 4 2.01 4.89 2.01 6L2 18C2 19.11 2.89 20 4 20H20C21.11 20 22 19.11 22 18V6C22 4.89 21.11 4 20 4ZM20 18H4V12H20V18ZM20 8H4V6H20V8Z"
      fill={isActive ? 'white' : '#005A9C'}
    />
  </svg>
);

const LogoutIcon: React.FC<{ isActive: boolean }> = ({ isActive }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.sidebarIcon}
  >
    <path
      d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.59L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z"
      fill="currentColor"
    />
  </svg>
);

interface SidebarProps {
  user?: User | null;
  onLogout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ user, onLogout }) => {
  const [collapsed, setCollapsed] = React.useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const location = useLocation();
  const userRole = useSelector(selectUserRole);
  const session = useSelector(selectSession);
  const logo = useSelector(selectLogo);

  // Use user from Redux session if prop is not provided
  const displayUser = user || session?.user;

  // Filter and rename links based on role
  const links = useMemo(() => {
    const baseLinks = [{ name: 'Dashboard', icon: DashboardIcon, path: '/dashboard' }];

    // Show Admin Panel only for OUTLET_MANAGER and ORG_ADMIN
    if (userRole === 'OUTLET_MANAGER' || userRole === 'ORG_ADMIN') {
      baseLinks.push({ name: 'Admin Panel', icon: AdminIcon, path: '/admin' });
    }

    // Billing section - rename to "History" for POC
    const billingName = userRole === 'OUTLET_POC' ? 'History' : 'Billing';
    const billingIcon = userRole === 'OUTLET_POC' ? HistoryIcon : BillingIcon;
    baseLinks.push({ name: billingName, icon: billingIcon, path: '/billing' });

    return baseLinks;
  }, [userRole]);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const toggleSidebar = () => {
    setCollapsed((prev: boolean) => {
      const newState = !prev;
      localStorage.setItem('sidebar-collapsed', JSON.stringify(newState));
      return newState;
    });
  };

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`}>
      <div className={`${styles.sidebarHeader} ${collapsed ? styles.sidebarHeaderCollapsed : ''}`}>
        {!collapsed && (
          <h1 className={`${styles.sidebarLogoText} brand-text-gradient`} data-testid="sidebar-logo">
            ShaktiSetu
          </h1>
        )}
        <button
          onClick={toggleSidebar}
          className={`${styles.toggleButton} ${collapsed ? styles.toggleButtonCollapsed : styles.toggleButtonExpanded}`}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.59 16.59L13.17 12L8.59 7.41L10 6L16 12L10 18L8.59 16.59Z" fill="currentColor" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.41 16.59L10.83 12L15.41 7.41L14 6L8 12L14 18L15.41 16.59Z" fill="currentColor" />
            </svg>
          )}
        </button>
      </div>
      <nav className={styles.sidebarNav}>
        {links.map((link) => {
          const IconComponent = link.icon;
          const active = isActive(link.path);
          return (
            <Link
              key={link.name}
              to={link.path}
              className={`${styles.sidebarLink} ${active ? styles.sidebarLinkActive : ''} ${collapsed ? styles.sidebarLinkCollapsed : ''}`}
              title={collapsed ? link.name : undefined}
            >
              <IconComponent isActive={active} />
              {!collapsed && <span>{link.name}</span>}
            </Link>
          );
        })}
      </nav>
      <div className={styles.sidebarFooter}>
        <div className={`${styles.userInfo} ${collapsed ? styles.userInfoCollapsed : ''}`} data-testid="sidebar-user-info">
          {logo ? (
            <img src={logo} alt="Org Logo" className={styles.userRoleLogo} />
          ) : (
            <div className={styles.userAvatar} data-testid="sidebar-user-avatar">
              {(
                displayUser?.user_metadata?.full_name ||
                displayUser?.user_metadata?.name ||
                displayUser?.email ||
                'User'
              )
                .charAt(0)
                .toUpperCase()}
            </div>
          )}
          {!collapsed && (
            <div className={styles.userDetails}>
              <p className={styles.userInfoName} data-testid="sidebar-user-name">
                {displayUser?.user_metadata?.full_name ||
                  displayUser?.user_metadata?.name ||
                  displayUser?.email ||
                  'User'}
              </p>
            </div>
          )}
        </div>
        <button
          onClick={onLogout}
          className={`ss-button-primary ${styles.logoutButton} ${collapsed ? styles.logoutButtonCollapsed : ''}`}
          data-testid="logout-button"
        >
          <LogoutIcon isActive={true} />
          {!collapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
